<template>
  <div class="sm-mail-edit">
    <template v-if="curItem && curPMB">
      <div class="sm-mail-edit-inner">
        <breadcrumbs-block 
          v-if="!isMobile"
          :crumbs="breadcrumbs"
        />
        <form
          v-if="curItem && curPMB"
          class="sm-form sm-form__mail-edit"
          @submit.prevent="handleSubmit"
        >
          <h3>Edit mail #{{item.mail_number}}</h3>
          <datepicker-wrap
            v-model="curItem.received_at"
            :val="curItem.received_at"
            :placeholder="new Date() | moment('MMMM D, YYYY')"
            :label="'Received on'"
          />
          <input-pmb-tags
            :label="'Mailbox Number'"
            :placeholder="'Name, Mailbox# or Company name'"
            :defaultTag="curPMB"
            :class="'disabled'"
            :error="errors.first('account_numbers')"
            @updateList="updatePMBList($event)"
          />

          <p
            v-if="!form1583Signed"
            class="sm-mail-edit__sign-error sm-small"
          >
            <include-svg :name="'icon-warning'" />
            <span class="sm-error sm-txt-strong">USPS Form 1583 has not been signed.</span>
            <a
              href="#"
              @click.prevent="openCustomer()"
            >Upload</a>
          </p>
          <p
            v-if="!formCA17853Signed"
            class="sm-mail-edit__sign-error sm-small"
          >
            <include-svg :name="'icon-warning'" />
            <span class="sm-error sm-txt-strong">CA Document 17538.5 has not been signed.</span>
            <a
              href="#"
              @click.prevent="openCustomer()"
            >Upload</a>
          </p>

          <input-text
            v-model="curItem.sender"
            :label="'Sender'"
            :name="'sender'"
            v-validate="'required'"
            data-vv-as="Sender"
            :error="errors.first('sender')"
          />
          <div class="sm-form__mail-edit-flex">
            <dropdown-wrap
              v-model="selectedType"
              :val="mailTypes.find(type => type.name == curItem.type)"
              :options="mailTypes"
              :capitalize="true"
              :placeholder="'Select mail type...'"
              :label="'Mail Type'"
              :class="'mail-type'"
              @input="curItem.mail_type_id = selectedType.id"
            />
            <checkbox-block
              v-model="curItem.is_scannable"
              :text="'Scannable'"
            />
          </div>
          <div class="sm-form__mail-edit-flex">
            <input-number
              v-model="curItem.quantity"
              :defaultVal="item.quantity || 1"
              :min="1"
              :label="'Quantity'"
            />
            <div class="sm-form__mail-edit-flex-weight">
              <dropdown-wrap
                v-model="selectedWeightUnit"
                :val="weightUnits.find(unit => unit.code == curItem.weight_unit) || weightUnits[0]"
                :options="weightUnits"
                :capitalize="true"
                :label="'Weight'"
                @input="curItem.weight_unit = selectedWeightUnit.code"
              />
              <input-number
                v-model="curItem.weight"
                :defaultVal="item.weight || 0"
                :min="0"
                :showControls="false"
              />
            </div>
          </div>
          <input-text
            v-model="curItem.tracking"
            :label="'Tracking number'"
            :name="'tracking'"
          />
          <dropdown-wrap
            v-model="selectedMessage"
            :val="selectedMessage"
            :options="messagesList"
            :placeholder="'Add message'"
            :label="'Message'"
            @input="selectMessage"
          />
          <template v-if="selectedMessage.value">
            <div
              v-if="selectedMessage.value != 'custom'"
              class="sm-form__new-mail-message"
            >{{selectedMessage.text}}</div>
            <textarea
              v-else
              v-model="curItem.comments"
              class="sm-form__new-mail-textarea"
              placeholder="Message..."
            ></textarea>
          </template>
        </form>
      </div>
      <div class="sm-mail-edit-inner">
        <h3>Update mail status</h3>
        <div
          v-for="action in curActions"
          :key="action.name" 
          class="sm-mail-edit__action"
        >
          <div class="sm-mail-edit__action-icon">
            <include-svg :name="action.icon" />
          </div>
          <div
            v-if="action.pending_field" 
            class="sm-mail-edit__action-switch"
          >
            <span>{{action.title}} requested</span>
            <label 
              class="sm-switcher"
              :class="{
                'disabled': action.name == 'forward' && item[action.complete_field]
              }"
            >
              <input 
                v-model="curItem[action.pending_field]"
                type="checkbox" 
              />
              <span></span>
            </label>
          </div>
          <div
            v-if="action.complete_field" 
            class="sm-mail-edit__action-switch"
          >
            <template v-if="!action.link_to_action || item[action.complete_field]">
              <span v-if="action.complete_title">{{action.complete_title}}</span>
              <span v-else>{{action.title}} complete</span>
              <span
                  v-if="action.name != 'signed' && action.result_date && curItem[action.result_date]"
                  class="disabled"
                >{{curItem[action.result_date] | moment('D MMMM, YYYY HH:mm')}}</span>
            </template>
            <template v-else>
              <a
                href="#"
                class="sm-link"
                @click.prevent="openActionLink(action.link_to_action)"
              >
                <span v-if="action.complete_title">{{action.complete_title}}</span>
                <span v-else>{{action.title}} complete</span>
              </a>
            </template>
            <label 
              class="sm-switcher"
              :class="{
                'disabled': action.name == 'forward'
              }"
            >
              <input 
                v-model="curItem[action.complete_field]"
                type="checkbox" 
              />
              <span></span>
            </label>
          </div>
          <template v-if="action.result_field && curItem[action.complete_field] && action.upload_options">
            <template v-if="!action.upload_options.no_upload">
              <upload-file
                v-if="!curItem[action.result_field]"
                :class="{
                  'error': errors.first(action.name)
                }"
                :name="action.name"
                :types="action.upload_options.types"
                :typesText="action.upload_options.typesText"
                :showPreview="'image'"
                @upload="file => uploadFile(file, action.name)"
                @delete="deleteFile(action.name)"
              />
              <div
                v-else
                class="sm-mail-edit__action-zone"
              >
                <a
                  :href="curItem[action.result_field]"
                  target="_blank"
                >
                  <img-preview
                    :src="curItem[action.result_field]"
                  />
                </a>
                <span v-if="curItem[action.upload_options.date_field]">Uploaded: {{curItem[action.upload_options.date_field] | moment('D MMM, YYYY')}}</span>
                <a
                  href="#"
                  class="sm-link"
                  @click.prevent="updateFile(action)"
                >Update</a>
              </div>
            </template>
            <template v-else>
              <div class="sm-mail-edit__action-result">
                <a
                  :href="curItem[action.result_field]"
                  target="_blank"
                >
                  <img-preview
                    :src="curItem[action.result_field]"
                  />
                </a>
                <span
                  v-if="action.result_date && curItem[action.result_date]"
                  class="disabled"
                >{{curItem[action.result_date] | moment('D MMMM, YYYY HH:mm')}}</span>
              </div>
            </template>
          </template>
          <ul 
            v-if="action.additional"
            class="sm-mail-edit__action-additional"
          >
            <li
              v-for="el in action.additional"
              :key="el.field"
              :class="{
                'disabled': item[action.complete_field]
              }"
            >
              <input-text
                v-if="el.type == 'price-input'"
                :label="el.label || ''"
                :value="formatPrice(curItem[el.field])"
                @blur="e => processPriceValue(e, el.field)"
              />
              <checkbox-block
                v-else-if="el.type == 'checkbox'"
                :text="el.label"
                v-model="curItem[el.field]"
              />
            </li>
          </ul>
        </div>
        <div class="sm-mail-edit__controls">
          <div class="sm-wrap sm">
            <div class="sm-mail-edit__controls-inner">
              <btn-block
                :classes="['sm-btn--small']"
                :text="'Update mail'"
                @click="handleSubmit"
              />
              <btn-block
                v-if="haveChanges"
                :classes="['sm-btn--small', 'sm-btn--grey', 'discard']"
                :text="'Discard changes'"
                @click="resetItem"
              />
              <btn-block
                :classes="['sm-btn--small', 'sm-btn--error']"
                :text="'Delete mail'"
                :icon="'icon-mail-delete'"
                @click="popup.isOpen = true"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent">
        <confirm-block
          :text="`Mail Item #${item.mail_number} will be deleted.`"
          :confirmType="'error'"
          @confirm="deleteItem"
          @cancel="popup.isOpen = false"
        />
      </template>
    </popup-block>

    <sm-spinner :show="(!curItem && !curPMB) || showSpinner" />
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs';
import InputText from '@/components/InputText';
import InputNumber from '@/components/InputNumber';
import InputPMBTags from '@/components/mail/InputPMBTags';
import DropdownWrap from '@/components/DropdownWrap';
import Checkbox from '@/components/Checkboxes';
import DatepickerWrap from '@/components/DatepickerWrap';
import UploadFile from '@/components/UploadFile';
import ImgPreview from '@/components/ImgPreview';
import Spinner from '@/components/Spinner';
import Popup from '@/components/Popup';
import Confirm from '@/components/Confirm';
import { mapActions, mapGetters } from 'vuex';
import {FormatPrice} from '@/utils/general-data';
import {ProcessInputValue} from '@/utils/input';
import WeightUnits from '@/settings/weight-units';
import MessagesList from '@/settings/messages-list';

export default {
  name: 'mail-edit',
  components: {
    'sm-spinner': Spinner,
    'breadcrumbs-block': Breadcrumbs,
    'input-text': InputText,
    'input-number': InputNumber,
    'input-pmb-tags': InputPMBTags,
    'dropdown-wrap': DropdownWrap,
    'checkbox-block': Checkbox,
    'datepicker-wrap': DatepickerWrap,
    'upload-file': UploadFile,
    'img-preview': ImgPreview,
    'popup-block': Popup,
    'confirm-block': Confirm
  },
  data() {
    return {
      item: null,
      curItem: null,
      curPMB: null,
      curCustomer: null,
      showSpinner: false,
      breadcrumbs: [
        {
          link: 'mail?tab=single',
          text: 'Mail Single'
        },
        {
          link: '/mail-edit',
          text: 'Edit mail',
          current: true
        }
      ],
      selectedMessage: {},
      selectedWeightUnit: null,
      selectedType: {},
      weightUnits: [],
      messagesList: [],
      actions: [
        {
          name: 'pick_up',
          title: 'Pick up',
          icon: 'icon-action-mail-pick-customer',
          pending_field: 'is_pick_up',
          complete_field: 'is_pick_up_complete',
          result_date: 'pick_up_scheduled_at',
        },
        {
          name: 'signed',
          title: 'Signed with',
          complete_title: 'Signed with',
          icon: 'icon-action-signed',
          complete_field: 'is_signed',
          result_field: 'signature_url',
          result_date: 'signed_on',
          upload_options: {
            no_upload: true
          }
        },
        {
          name: 'picture',
          title: 'Envelope picture',
          icon: 'icon-action-foto',
          pending_field: 'picture_requested_at',
          complete_field: 'picture_added_at',
          result_field: 'picture_url',
          upload_options: {
            types: ['image', 'pdf'],
            upload_date_field: 'picture_added_at',
            typesText: 'JPG, JPEG, PNG, GIF, HEIC, HEIF or PDF'
          }
        },
        {
          name: 'scan',
          title: 'Scan',
          icon: 'icon-action-scan',
          pending_field: 'is_scan_requested',
          complete_field: 'is_scan_complete',
          result_field: 'scan_url',
          upload_options: {
            types: ['pdf', 'image'],
            date_field: 'scanned_at',
            typesText: 'JPG, JPEG, PNG, GIF, HEIC, HEIF or PDF'
          }
        },
        {
          name: 'forward',
          title: 'Forward',
          complete_title: 'Forward',
          link_to_action: 'mail-forwarding',
          icon: 'icon-action-forward',
          pending_field: 'is_forward_requested',
          complete_field: 'is_forward_complete',
          additional: [
            {
              field: 'forward_price',
              type: 'price-input',
              label: 'Enter postage amount'
            },
            {
              field: 'international_forwarding',
              type: 'checkbox',
              label: 'International forwarding'
            }
          ]
        },
        {
          name: 'shred',
          title: 'Shred',
          icon: 'icon-action-shred',
          pending_field: 'is_shred_requested',
          complete_field: 'is_shred_complete'
        },
        {
          name: 'mark_as_pick_up',
          title: 'Mark as picked up',
          complete_title: 'Confirm pick complete by the user',
          icon: 'icon-action-mail-pick',
          pending_field: 'mark_as_pick_up_requested',
          complete_field: 'mark_as_pick_up_complete'
        },
        {
          name: 'check_deposit',
          title: 'Check deposit',
          icon: 'icon-action-check-deposit',
          pending_field: 'is_check_deposit_requested',
          complete_field: 'is_check_deposit_completed'
        },
      ],
      files: {
        scan: null,
        picture: null
      },
      popup: {
        isOpen: false
      },
      form1583Signed: true,
      formCA17853Signed: true
    }
  },
  computed: {
    ...mapGetters({
      isMobile: 'isMobile',
      mailTypes: 'mailTypes',
      provider: 'provider'
    }),
    haveChanges() {
      if (!this.item || !this.curItem)
        return false;

      return Object.keys(this.curItem).some(key => {
        if (key == 'is_signed')
          return !!this.curItem[key] != !!this.item.signed_on;

        if (key == 'weight')
          return !!this.curItem[key] != !!this.item[key];

        return !this.item.hasOwnProperty(key) || this.curItem[key] != this.item[key];
      });
    },
    curActions() {
      if (!this.curItem.signed_on)
        return this.actions.filter(item => item.name != 'signed');

      return this.actions;
    }
  },
  created() {
    this.weightUnits = WeightUnits;
    this.messagesList = MessagesList;
    let mail_number = this.$route.query.mail_number;

    if (!mail_number)
      this.$router.push('mail');

    Promise.all([
      this.getAdminMail(mail_number),
      this.getMailTypes()
    ])
      .then(([mail, mail_types]) => { 
        this.item = mail.mail_item;
          
        if (this.item.comments)
          this.setDefaultComments();

        this.curItem = Object.assign({}, {
          ...this.item,
          is_signed: this.item.signed_on ? true : false,
          weight: this.item.weight || 0,
          received_at: this.item.received_at.split('T')[0]//this.$moment(this.item.received_at).format('YYYY-MM-DD')
        });

        Promise.all([
          this.findPMBs(this.curItem.account_number),
          this.getCustomer(this.curItem.account_id)
        ])
          .then(([pmbs, customer]) => {
            this.curCustomer = customer;
            this.checkFormStates();
            this.curPMB = pmbs.find(item => item.pmb == this.curItem.account_number);
          });
      })
      .catch(e => {
        if (e.response.status === 422) {
          this.$router.push('mail');
        }
      });
  },
  methods: {
    ...mapActions({
      getAdminMail: 'mail/getAdminMail',
      findPMBs: 'mail/findPMBs',
      getMailTypes: 'getMailTypes',
      editMail: 'mail/editMail',
      deleteMail: 'mail/deleteMail',
      getCustomer: 'customers/getCustomer'
    }),
    formatPrice: FormatPrice,
    handleSubmit() {
      this.$validator.validate()
        .then(valid => {
          let sendData = {};

          Object.keys(this.curItem).forEach(key => {
            if (key == 'received_at' && this.isSameDate(this.item.received_at, this.curItem.received_at))
              return;

            if (!this.item.hasOwnProperty(key) || this.curItem[key] != this.item[key])
              sendData[key] = this.curItem[key];
          });

          if (sendData.weight == 0 && this.item.weight == null)
            delete sendData.weight;

          if (sendData.is_scan_complete && !this.item.scan_url) {
            if (this.files.scan) {
              sendData.scan = this.files.scan;
            } else {
              this.errors.add({
                field: 'scan',
                msg: 'Please upload scan'
              });
              valid = false;
            }
          }

          if (sendData.hasOwnProperty('is_scan_complete') && !sendData.is_scan_complete && this.item.scan_url)
            sendData.scan = null;

          if (sendData.hasOwnProperty('picture_requested_at')) { // API field not matching
            sendData.is_picture_requested = sendData.picture_requested_at;
            delete sendData.picture_requested_at;
          }

          if (sendData.hasOwnProperty('picture_added_at')) {
            sendData.is_picture_added = sendData.picture_added_at;
            delete sendData.picture_added_at;

            if (sendData.is_picture_added) {
              if (this.files.picture) {
                sendData.picture = this.files.picture
              } else {
                this.errors.add({
                  field: 'picture',
                  msg: 'Please upload picture'
                });
                valid = false;
              }
            }
          }

          if (sendData.hasOwnProperty('picture_url') && this.files.picture) {
            sendData.picture = this.files.picture;
            sendData.is_picture_added = true;
            delete sendData.picture_url;
          }

          if (sendData.hasOwnProperty('scan_url') && this.files.scan) {
            sendData.scan = this.files.scan;
            sendData.is_scan_complete = true;
            delete sendData.scan_url;
          }

          if (sendData.hasOwnProperty('is_signed')) {
            if (
              (!sendData.is_signed && this.curItem.signed_on) ||
              (sendData.hasOwnProperty('is_pick_up_complete') && !sendData.is_pick_up_complete)
            ) {
              sendData.signed_on = null;
            }

            delete sendData.is_signed;
          }

          if (sendData.hasOwnProperty('received_at') && !sendData.received_at)
            sendData.received_at = this.$moment(new Date()).format('YYYY-MM-DD');

          if (sendData.weight_unit && !sendData.weight)
            sendData.weight = this.curItem.weight;
          

          if (!valid)
            return;

          if (Object.keys(sendData).length) {
            let formData = new FormData();

            Object.keys(sendData).forEach(key => {
              formData.append(`mail_item[${key}]`, sendData[key]);
            });

            this.showSpinner = true;
            this.editMail({
              id: this.curItem.id,
              params: formData
            })
              .then(res => {
                if ( // overcome remove scan bug, crunchy!
                  sendData.hasOwnProperty('scan') &&
                  !sendData.scan &&
                  sendData.hasOwnProperty('is_scan_complete') &&
                  !sendData.is_scan_complete &&
                  res.mail_item.is_scan_complete
                ) {
                  formData = new FormData();
                  formData.append('mail_item[is_scan_complete]', false);

                  this.editMail({
                    id: this.curItem.id,
                    params: formData
                  })
                }


                this.$router.push('mail');
                this.showSpinner = false;
              });
          } else {
            this.$router.push('mail');
          } 
        });
    },
    resetItem() {
      this.curItem = Object.assign({}, {
        ...this.item,
        is_signed: this.item.signed_on ? true : false,
        weight: this.item.weight || 0
      });
      this.$router.push('mail');
    },
    deleteItem() {
      this.deleteMail(this.item.id)
        .then(res => {
          this.$router.push('mail');
        });
    },
    selectMessage() {
      if (this.selectedMessage.value != 'custom') {
        this.curItem.comments = this.selectedMessage.text;
      } else {
        this.curItem.comments = '';
      }
    },
    updatePMBList(list) {
      this.curItem.account_number = list[0];

      if (list[0])
        this.errors.remove('account_numbers');
    },
    processPriceValue(e, field) {
      this.curItem[field] = ProcessInputValue(e, 'price');
    },
    uploadFile(file, name) {
      this.files[name] = file;
      this.errors.remove(name);
    },
    deleteFile(name) {
      this.files[name] = null;
    },
    updateFile(action) {
      this.curItem[action.result_field] = null;
    },
    checkFormStates() {
      if (!this.curCustomer || !this.provider || this.provider.country_id != 1 || this.provider.client_level == 1)
        return;

      if (!this.curCustomer.is_form_1583_signed)
        this.form1583Signed = false;

      if (this.provider.state_id == 5 && !this.curCustomer.is_ca_form_17853_signed)
        this.formCA17853Signed = false;
    },
    openCustomer() {
      this.$router.push(`customer/${this.curCustomer.id}?from=${this.curItem.mail_number}`);
    },
    openActionLink(actionName) {
      this.$router.push(`${actionName}?mail_number=${this.item.mail_number}`);
    },
    setDefaultComments() {
      let currentMessage = this.messagesList.find(message => message.text == this.item.comments);

      if (!currentMessage)
        currentMessage = this.messagesList.find(message => message.value == 'custom');

      this.selectedMessage = currentMessage;
    },
    isSameDate(date1, date2) {
      let d1 = new Date(date1);
      let d2 = new Date(date2);

      return (
        d1.getFullYear() == d2.getFullYear() &&
        d1.getMonth() == d2.getMonth() &&
        d1.getDay() == d2.getDay()
      )
    }
  },
  watch: {
    'curItem.is_scan_complete'(val) {
      if (!val)
        this.errors.remove('scan');
    },
    'curItem.picture_added_at'(val) {
      if (!val)
        this.errors.remove('picture');
    }
  }
}
</script>

<style lang="scss">
  @use '@/assets/scss/general/mixins.scss' as *;
  @use '@/assets/scss/framework/scss-variables.scss' as *;
  @use '@/assets/scss/pages/common/_mail-edit.scss';
</style>